import type { Helpers } from "./endpoints-shared";
import type { In, Out } from "./operations";
import type { PageRequest } from "./types";

/**
 * The general ledger: chart, departments, fiscal calendar, journals and drafts, reports, the billing bridge, deposits and deferrals.
 * Every route is under `/accounting` and needs the `accounting` module in the license. (The billing-run screens of
 * `endpoints-accounting.ts` are a different thing: they live under `/billing`.)
 */
export function ledgerEndpoints({ get, post, patch, del, paged }: Helpers) {
  const a = "/accounting";
  return {
    // ---- chart of accounts
    listAccounts: (
      page: PageRequest & {
        q?: string | undefined;
        type?: In<"accounting_create_account">["type"] | undefined;
        subtype?: In<"accounting_create_account">["subtype"] | undefined;
        control?: In<"accounting_create_account">["control"] | undefined;
        active?: boolean | undefined;
        isPosting?: boolean | undefined;
      } = {},
    ) =>
      get<Out<"accounting_list_accounts">>(
        `${a}/accounts`,
        paged(page, { q: page.q, type: page.type, subtype: page.subtype, control: page.control, active: page.active, is_posting: page.isPosting }),
      ),
    getAccount: (id: string, signal?: AbortSignal) => get<Out<"accounting_get_account">>(`${a}/accounts/${id}`, { signal }),
    /** Needs `accounting.settings`. */
    createAccount: (input: In<"accounting_create_account">) => post<Out<"accounting_create_account">>(`${a}/accounts`, input),
    updateAccount: (id: string, input: In<"accounting_update_account">) => patch<Out<"accounting_update_account">>(`${a}/accounts/${id}`, input),

    // ---- departments
    listDepartments: (signal?: AbortSignal) => get<Out<"accounting_list_departments">>(`${a}/departments`, { signal }),
    createDepartment: (input: In<"accounting_create_department">) => post<Out<"accounting_create_department">>(`${a}/departments`, input),
    updateDepartment: (id: string, input: In<"accounting_update_department">) =>
      patch<Out<"accounting_update_department">>(`${a}/departments/${id}`, input),

    // ---- settings
    readSettings: (signal?: AbortSignal) => get<Out<"accounting_read_settings">>(`${a}/settings`, { signal }),
    updateSettings: (input: In<"accounting_update_settings">) => patch<Out<"accounting_update_settings">>(`${a}/settings`, input),

    // ---- fiscal calendar
    listFiscalYears: (signal?: AbortSignal) => get<Out<"accounting_list_fiscal_years">>(`${a}/fiscal-years`, { signal }),
    addFiscalYear: (input: In<"accounting_add_fiscal_year">) => post<Out<"accounting_add_fiscal_year">>(`${a}/fiscal-years`, input),
    listPeriodEvents: (id: string, signal?: AbortSignal) => get<Out<"accounting_list_period_events">>(`${a}/periods/${id}/events`, { signal }),
    /** Needs `accounting.close`. */
    startClosing: (id: string, input: In<"accounting_start_closing_period">) =>
      post<Out<"accounting_start_closing_period">>(`${a}/periods/${id}/start-closing`, input),
    cancelClosing: (id: string, input: In<"accounting_cancel_closing_period">) =>
      post<Out<"accounting_cancel_closing_period">>(`${a}/periods/${id}/cancel-closing`, input),
    closePeriod: (id: string, input: In<"accounting_close_period">) => post<Out<"accounting_close_period">>(`${a}/periods/${id}/close`, input),
    /** Needs `accounting.reopen`; a reason is required. */
    reopenPeriod: (id: string, input: In<"accounting_reopen_period">) => post<Out<"accounting_reopen_period">>(`${a}/periods/${id}/reopen`, input),
    /** Needs `accounting.lock`. Final. */
    lockPeriod: (id: string, input: In<"accounting_lock_period">) => post<Out<"accounting_lock_period">>(`${a}/periods/${id}/lock`, input),

    // ---- journal drafts and approval
    listDrafts: (page: PageRequest & { status?: Out<"accounting_get_draft">["status"] | undefined } = {}) =>
      get<Out<"accounting_list_drafts">>(`${a}/journal-drafts`, paged(page, { status: page.status })),
    getDraft: (id: string, signal?: AbortSignal) => get<Out<"accounting_get_draft">>(`${a}/journal-drafts/${id}`, { signal }),
    /** Needs `accounting.journals.write`. */
    createDraft: (input: In<"accounting_create_draft">) => post<Out<"accounting_create_draft">>(`${a}/journal-drafts`, input),
    updateDraft: (id: string, input: In<"accounting_update_draft">) => patch<Out<"accounting_update_draft">>(`${a}/journal-drafts/${id}`, input),
    deleteDraft: (id: string) => del(`${a}/journal-drafts/${id}`),
    submitDraft: (id: string, input: In<"accounting_submit_draft">) => post<Out<"accounting_submit_draft">>(`${a}/journal-drafts/${id}/submit`, input),
    recallDraft: (id: string) => post<Out<"accounting_recall_draft">>(`${a}/journal-drafts/${id}/recall`),
    /** Needs `accounting.journals.approve`. Posts the journal. */
    approveDraft: (id: string, input: In<"accounting_approve_draft">) => post<Out<"accounting_approve_draft">>(`${a}/journal-drafts/${id}/approve`, input),
    rejectDraft: (id: string, input: In<"accounting_reject_draft">) => post<Out<"accounting_reject_draft">>(`${a}/journal-drafts/${id}/reject`, input),

    // ---- posted journals
    listJournals: (
      page: PageRequest & { fromDate?: string | undefined; toDate?: string | undefined; sourceModule?: string | undefined; kind?: string | undefined; q?: string | undefined } = {},
    ) =>
      get<Out<"accounting_list_journals">>(
        `${a}/journals`,
        paged(page, { from_date: page.fromDate, to_date: page.toDate, source_module: page.sourceModule, kind: page.kind, q: page.q }),
      ),
    getJournal: (id: string, signal?: AbortSignal) => get<Out<"accounting_get_journal">>(`${a}/journals/${id}`, { signal }),
    /** Needs `accounting.reverse`. */
    reverseJournal: (id: string, input: In<"accounting_reverse">) => post<Out<"accounting_reverse">>(`${a}/journals/${id}/reverse`, input),
    /** The billing lines, deferral months or deposited payments behind one journal line. 409 `no_sources` for other journals. */
    lineSources: (journalId: string, lineNo: number, signal?: AbortSignal) =>
      get<Out<"accounting_journal_line_sources">>(`${a}/journals/${journalId}/lines/${lineNo}/sources`, { signal }),

    // ---- reports
    trialBalance: (query: { toDate?: string | undefined; fromDate?: string | undefined; byDepartment?: boolean | undefined } = {}, signal?: AbortSignal) =>
      get<Out<"accounting_trial_balance">>(`${a}/reports/trial-balance`, {
        query: { to_date: query.toDate, from_date: query.fromDate, by_department: query.byDepartment },
        signal,
      }),
    glDetail: (
      page: PageRequest & { accountId: string; departmentId?: string | undefined; fromDate?: string | undefined; toDate?: string | undefined },
    ) =>
      get<Out<"accounting_gl_detail">>(
        `${a}/reports/gl-detail`,
        paged(page, { account_id: page.accountId, department_id: page.departmentId, from_date: page.fromDate, to_date: page.toDate }),
      ),
    accountInquiry: (query: { accountId: string; departmentId?: string | undefined; fiscalYearId?: string | undefined }, signal?: AbortSignal) =>
      get<Out<"accounting_account_inquiry">>(`${a}/reports/account-inquiry`, {
        query: { account_id: query.accountId, department_id: query.departmentId, fiscal_year_id: query.fiscalYearId },
        signal,
      }),

    // ---- the billing bridge
    listPostingRules: (includeRetired = false, signal?: AbortSignal) =>
      get<Out<"accounting_list_posting_rules">>(`${a}/posting-rules`, { query: { include_retired: includeRetired }, signal }),
    /** Needs `accounting.settings`. */
    createPostingRule: (input: In<"accounting_create_posting_rule">) => post<Out<"accounting_create_posting_rule">>(`${a}/posting-rules`, input),
    /** Returns the successor rule, with a new id. */
    updatePostingRule: (id: string, input: In<"accounting_update_posting_rule">) =>
      patch<Out<"accounting_update_posting_rule">>(`${a}/posting-rules/${id}`, input),
    retirePostingRule: (id: string) => del(`${a}/posting-rules/${id}`),
    testPostingRule: (input: In<"accounting_test_posting_rule">) => post<Out<"accounting_test_posting_rule">>(`${a}/posting-rules/test`, input),
    bridgeStatus: (signal?: AbortSignal) => get<Out<"accounting_bridge_status">>(`${a}/bridge/status`, { signal }),
    /** Needs `accounting.settings`. 409 `gl_not_started`, `bridge_busy`. */
    runBridgeNow: () => post<Out<"accounting_run_bridge_now">>(`${a}/bridge/run`),
    tieOuts: (asOf?: string, signal?: AbortSignal) => get<Out<"accounting_tie_out_check">>(`${a}/tie-outs`, { query: { as_of: asOf }, signal }),
    memberArTieOut: (asOf?: string, signal?: AbortSignal) =>
      get<Out<"accounting_member_ar_tie_out">>(`${a}/tie-outs/member-ar`, { query: { as_of: asOf }, signal }),

    // ---- deferred revenue
    listDeferralRules: (signal?: AbortSignal) => get<Out<"accounting_list_deferral_rules">>(`${a}/deferral-rules`, { signal }),
    createDeferralRule: (input: In<"accounting_create_deferral_rule">) => post<Out<"accounting_create_deferral_rule">>(`${a}/deferral-rules`, input),
    updateDeferralRule: (id: string, input: In<"accounting_update_deferral_rule">) =>
      patch<Out<"accounting_update_deferral_rule">>(`${a}/deferral-rules/${id}`, input),
    deferralSchedule: (signal?: AbortSignal) => get<Out<"accounting_deferral_schedule">>(`${a}/deferrals/schedule`, { signal }),
    /** Needs `accounting.close`. */
    recogniseDeferrals: (input: In<"accounting_recognise_now">) => post<Out<"accounting_recognise_now">>(`${a}/deferrals/recognise`, input),

    // ---- bank deposits and card payouts
    depositablePayments: (signal?: AbortSignal) => get<Out<"accounting_depositable_payments">>(`${a}/deposits/payments`, { signal }),
    listDeposits: (page: PageRequest & { status?: "draft" | "finalised" | "voided" | undefined } = {}) =>
      get<Out<"accounting_list_deposits">>(`${a}/deposits`, paged(page, { status: page.status })),
    getDeposit: (id: string, signal?: AbortSignal) => get<Out<"accounting_get_deposit">>(`${a}/deposits/${id}`, { signal }),
    /** The writes below need `accounting.bank.deposits`. */
    createDeposit: (input: In<"accounting_create_deposit">) => post<Out<"accounting_create_deposit">>(`${a}/deposits`, input),
    updateDeposit: (id: string, input: In<"accounting_update_deposit">) => patch<Out<"accounting_update_deposit">>(`${a}/deposits/${id}`, input),
    deleteDeposit: (id: string) => del(`${a}/deposits/${id}`),
    finaliseDeposit: (id: string, input: In<"accounting_finalise_deposit">) => post<Out<"accounting_finalise_deposit">>(`${a}/deposits/${id}/finalise`, input),
    voidDeposit: (id: string, input: In<"accounting_void_deposit">) => post<Out<"accounting_void_deposit">>(`${a}/deposits/${id}/void`, input),
    listCardPayouts: (page: PageRequest = {}) => get<Out<"accounting_list_card_payouts">>(`${a}/card-payouts`, paged(page)),
    recordCardPayout: (input: In<"accounting_record_card_payout">) => post<Out<"accounting_record_card_payout">>(`${a}/card-payouts`, input),
  };
}
